import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient, getFilterOptions } from '../services/clients';
import { getClientSources } from '../services/sources';
import { createAuditLog } from '../services/audit';
import { exportClientsToCSV, exportClientsToJSON } from '../utils/clientExport';
import { getCompanies, createCompany, updateCompany, deleteCompany, getCompanyWithClients, getCompany } from '../services/companies';
import CompanySearchSelect from '../components/CompanySearchSelect';
import ExcelSpreadsheet from '../components/ExcelSpreadsheet';
import './Clients.css';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ cities: [], states: [], zips: [] });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    source: '',
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [sources, setSources] = useState([]);
  const [activeTab, setActiveTab] = useState('clients'); // 'clients', 'companies', or 'historic-clients'
  
  // Companies state
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [companyClients, setCompanyClients] = useState([]);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    address: '',
  });

  const [filters, setFilters] = useState({
    search: '',
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    zip: '',
  });

  const navigate = useNavigate();

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClients();
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      alert('Failed to load clients: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  const loadSources = useCallback(async () => {
    try {
      const data = await getClientSources();
      setSources(data);
    } catch (error) {
      console.error('Error loading sources:', error);
      // Don't show error if table doesn't exist yet
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadFilterOptions();
    loadSources();
  }, [loadClients, loadFilterOptions, loadSources]);

  const loadCompanies = useCallback(async () => {
    try {
      setLoadingCompanies(true);
      const data = await getCompanies();
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      alert('Failed to load companies: ' + error.message);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'companies') {
      loadCompanies();
    }
  }, [activeTab, loadCompanies]);

  useEffect(() => {
    if (showForm) {
      loadCompanies();
    }
  }, [showForm, loadCompanies]);

  const handleViewCompany = async (companyId) => {
    try {
      const company = await getCompanyWithClients(companyId);
      setViewingCompany(company);
      setCompanyClients(company.clients || []);
    } catch (error) {
      console.error('Error loading company:', error);
      alert('Failed to load company: ' + error.message);
    }
  };

  const handleCloseCompanyView = () => {
    setViewingCompany(null);
    setCompanyClients([]);
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, companyFormData);
        await createAuditLog('update', 'company', editingCompany.id, { company_name: companyFormData.name });
      } else {
        const newCompany = await createCompany(companyFormData);
        await createAuditLog('create', 'company', newCompany.id, { company_name: companyFormData.name });
      }
      
      loadCompanies();
      handleCloseCompanyForm();
    } catch (error) {
      alert('Failed to save company: ' + error.message);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name || '',
      address: company.address || '',
    });
    setShowCompanyForm(true);
  };

  const handleDeleteCompany = async (id) => {
    if (window.confirm('Are you sure you want to delete this company? This will not delete associated clients.')) {
      try {
        await deleteCompany(id);
        await loadCompanies();
        if (viewingCompany && viewingCompany.id === id) {
          handleCloseCompanyView();
        }
      } catch (error) {
        alert('Failed to delete company: ' + error.message);
      }
    }
  };

  const handleCloseCompanyForm = () => {
    setShowCompanyForm(false);
    setEditingCompany(null);
    setCompanyFormData({
      name: '',
      address: '',
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...clients];

    // General search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(client =>
        Object.values(client).some(value =>
          value && value.toString().toLowerCase().includes(searchLower)
        )
      );
    }

    // Specific field filters
    if (filters.name) {
      filtered = filtered.filter(c =>
        c.name && c.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    if (filters.email) {
      filtered = filtered.filter(c =>
        c.email && c.email.toLowerCase().includes(filters.email.toLowerCase())
      );
    }
    if (filters.phone) {
      filtered = filtered.filter(c =>
        c.phone && c.phone.includes(filters.phone)
      );
    }
    if (filters.city) {
      filtered = filtered.filter(c =>
        c.city && c.city.toLowerCase().includes(filters.city.toLowerCase())
      );
    }
    if (filters.state) {
      filtered = filtered.filter(c =>
        c.state && c.state.toLowerCase().includes(filters.state.toLowerCase())
      );
    }
    if (filters.zip) {
      filtered = filtered.filter(c =>
        c.zip && c.zip.includes(filters.zip)
      );
    }

    setFilteredClients(filtered);
  }, [filters, clients]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      name: '',
      email: '',
      phone: '',
      city: '',
      state: '',
      zip: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let companyId = selectedCompany?.id;

      // Create new company if newCompanyName is provided (don't check if exists, just create it)
      if (!companyId && newCompanyName.trim()) {
        try {
          // Use the createCompany service function to ensure proper creation
          const newCompany = await createCompany({ name: newCompanyName.trim() });
          companyId = newCompany.id;
          
          // Add the new company to the local state immediately
          setCompanies(prev => [...prev, newCompany].sort((a, b) => a.name.localeCompare(b.name)));
          
          // Also reload companies list to ensure sync
          await loadCompanies();
        } catch (companyError) {
          // If company creation fails, still allow client creation without company
          console.error('Error creating company:', companyError);
          alert('Warning: Could not create company: ' + companyError.message + '\n\nClient will be created without company link.');
          companyId = null;
        }
      }

      // Prepare client data with company_id
      const clientData = {
        ...formData,
        company_id: companyId || null,
      };

      if (editingClient) {
        await updateClient(editingClient.id, clientData);
        await createAuditLog('update', 'client', editingClient.id, { client_name: formData.name });
      } else {
        const newClient = await createClient(clientData);
        await createAuditLog('create', 'client', newClient.id, { client_name: formData.name });
      }
      
      loadClients();
      loadFilterOptions();
      
      // If we're viewing a company and created/linked a client for it, reload the company view
      if (viewingCompany && (companyId === viewingCompany.id || (!companyId && editingClient?.company_id === viewingCompany.id))) {
        await handleViewCompany(viewingCompany.id);
      }
      
      handleCloseForm();
    } catch (error) {
      alert('Failed to save client: ' + error.message);
    }
  };

  const handleEdit = async (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip: client.zip || '',
      notes: client.notes || '',
      source: client.source || '',
    });
    
    // Load company if company_id exists
    if (client.company_id) {
      try {
        const company = await getCompany(client.company_id);
        setSelectedCompany(company);
      } catch (error) {
        console.error('Error loading company:', error);
        setSelectedCompany(null);
      }
    } else {
      setSelectedCompany(null);
    }
    
    setNewCompanyName('');
    setShowCreateCompany(false);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await deleteClient(id);
        await loadClients();
        await loadFilterOptions();
      } catch (error) {
        alert('Failed to delete client: ' + error.message);
      }
    }
  };

  const handleViewProfile = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setSelectedCompany(null);
    setNewCompanyName('');
    setShowCreateCompany(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      notes: '',
      source: '',
    });
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setNewCompanyName('');
    setShowCreateCompany(false);
  };

  const handleRemoveCompany = () => {
    setSelectedCompany(null);
    setNewCompanyName('');
    setShowCreateCompany(false);
  };

  const handleCreateClientForCompany = async (company) => {
    // Set the company as selected
    setSelectedCompany(company);
    setNewCompanyName('');
    setShowCreateCompany(false);
    
    // Reset form data
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      notes: '',
      source: '',
    });
    
    // Open the form
    setShowForm(true);
    
    // Switch to clients tab so user can see the new client after creation
    setActiveTab('clients');
  };

  const handleExportCSV = () => {
    try {
      const clientsToExport = filteredClients.length > 0 && hasActiveFilters ? filteredClients : clients;
      exportClientsToCSV(clientsToExport);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const handleExportJSON = () => {
    try {
      const clientsToExport = filteredClients.length > 0 && hasActiveFilters ? filteredClients : clients;
      exportClientsToJSON(clientsToExport);
    } catch (error) {
      console.error('Failed to export JSON:', error);
    }
  };


  const hasActiveFilters = Object.values(filters).some(f => f !== '');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="clients-page">
      <div className="clients-header">
        <h1>Clients</h1>
        <div className="header-actions-group">
          <div className="export-buttons">
            <button className="btn-export" onClick={handleExportCSV} title="Export to CSV">
              📥 Export CSV
            </button>
            <button className="btn-export" onClick={handleExportJSON} title="Export to JSON">
              📥 Export JSON
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Add Client
          </button>
        </div>
      </div>


      {/* Tab Navigation */}
      <div className="page-tabs">
        <button 
          className={activeTab === 'clients' ? 'active' : ''}
          onClick={() => setActiveTab('clients')}
        >
          Clients
        </button>
        <button 
          className={activeTab === 'companies' ? 'active' : ''}
          onClick={() => setActiveTab('companies')}
        >
          Companies
        </button>
        <button 
          className={activeTab === 'historic-clients' ? 'active' : ''}
          onClick={() => setActiveTab('historic-clients')}
        >
          Historic Client List
        </button>
      </div>

      {activeTab === 'companies' ? (
        <>
          {viewingCompany ? (
            <div className="company-detail-view">
              <div className="company-detail-header">
                <button className="btn-back" onClick={handleCloseCompanyView}>
                  ← Back to Companies
                </button>
                <div className="company-detail-info">
                  <h2>{viewingCompany.name}</h2>
                  {viewingCompany.address && (
                    <p className="company-address">{viewingCompany.address}</p>
                  )}
                </div>
                <div className="company-detail-actions">
                  <button className="btn-edit" onClick={() => handleEditCompany(viewingCompany)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDeleteCompany(viewingCompany.id)}>
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="company-clients-section">
                <div className="company-clients-header">
                  <h3>Clients ({companyClients.length})</h3>
                  <button className="btn-primary" onClick={() => handleCreateClientForCompany(viewingCompany)}>
                    + Create Client
                  </button>
                </div>
                {companyClients.length === 0 ? (
                  <div className="empty-state">No clients associated with this company</div>
                ) : (
                  <div className="table-container">
                    <table className="clients-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyClients.map(client => (
                          <tr key={client.id}>
                            <td>{client.name}</td>
                            <td>{client.email ? <a href={`mailto:${client.email}`} className="email-link">{client.email}</a> : '-'}</td>
                            <td>{client.phone ? <a href={`tel:${client.phone}`} className="phone-link">{client.phone}</a> : '-'}</td>
                            <td>{client.city || '-'}</td>
                            <td>{client.state || '-'}</td>
                            <td className="actions">
                              <button
                                className="btn-view"
                                onClick={() => handleViewProfile(client.id)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="companies-header-actions">
                <button className="btn-primary" onClick={() => setShowCompanyForm(true)}>
                  + Create Company
                </button>
              </div>

              {loadingCompanies ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                <div className="table-container">
                  <table className="clients-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Address</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="no-results">
                            No companies yet. Create your first company!
                          </td>
                        </tr>
                      ) : (
                        companies.map(company => (
                          <tr key={company.id}>
                            <td>{company.name}</td>
                            <td>{company.address || '-'}</td>
                            <td className="actions">
                              <button
                                className="btn-view"
                                onClick={() => handleViewCompany(company.id)}
                              >
                                View
                              </button>
                              <button
                                className="btn-edit"
                                onClick={() => handleEditCompany(company)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteCompany(company.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === 'historic-clients' ? (
        <ExcelSpreadsheet 
          onCreateClient={(newClient) => {
            loadClients();
            setActiveTab('clients'); // Switch to clients tab to see new client
          }}
          defaultTab="clients"
        />
      ) : (
        <>
      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-header">
          <button
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '▼' : '▶'} Filters
          </button>
          {hasActiveFilters && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear All Filters
            </button>
          )}
          <div className="result-count">
            Showing {filteredClients.length} of {clients.length} clients
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Quick Search (All Fields)</label>
                <input
                  type="text"
                  placeholder="Search all fields..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="filter-input"
                />
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Filter by name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Email</label>
                <input
                  type="text"
                  placeholder="Filter by email..."
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Phone</label>
                <input
                  type="text"
                  placeholder="Filter by phone..."
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                  className="filter-input"
                />
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>City</label>
                <input
                  type="text"
                  list="cities-list"
                  placeholder="Filter by city..."
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="filter-input"
                />
                <datalist id="cities-list">
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
              </div>
              <div className="filter-group">
                <label>State</label>
                <input
                  type="text"
                  list="states-list"
                  placeholder="Filter by state..."
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="filter-input"
                />
                <datalist id="states-list">
                  {filterOptions.states.map(state => (
                    <option key={state} value={state} />
                  ))}
                </datalist>
              </div>
              <div className="filter-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  list="zips-list"
                  placeholder="Filter by ZIP..."
                  value={filters.zip}
                  onChange={(e) => handleFilterChange('zip', e.target.value)}
                  className="filter-input"
                />
                <datalist id="zips-list">
                  {filterOptions.zips.map(zip => (
                    <option key={zip} value={zip} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Clients Table */}
      <div className="table-container">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-results">
                  {hasActiveFilters
                    ? 'No clients match your filters'
                    : 'No clients yet. Add your first client!'}
                </td>
              </tr>
            ) : (
              filteredClients.map(client => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td>{client.email ? <a href={`mailto:${client.email}`} className="email-link">{client.email}</a> : '-'}</td>
                  <td>{client.phone ? <a href={`tel:${client.phone}`} className="phone-link">{client.phone}</a> : '-'}</td>
                  <td>{client.city || '-'}</td>
                  <td>{client.state || '-'}</td>
                  <td className="actions">
                    <button
                      className="btn-view"
                      onClick={() => handleViewProfile(client.id)}
                    >
                      View
                    </button>
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(client)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(client.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      {/* Client Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="btn-close" onClick={handleCloseForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="client-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  >
                    <option value="">Select a source...</option>
                    {sources.map((source) => (
                      <option key={source.id} value={source.name}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Company (Optional)</label>
                  {selectedCompany ? (
                    <div className="company-selected">
                      <div className="company-selected-info">
                        <span className="company-name">{selectedCompany.name}</span>
                      </div>
                      <div className="company-selected-actions">
                        <button
                          type="button"
                          className="btn-change-company"
                          onClick={() => {
                            setSelectedCompany(null);
                            setShowCreateCompany(false);
                          }}
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          className="btn-remove-company"
                          onClick={handleRemoveCompany}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <CompanySearchSelect
                        onSelect={handleCompanySelect}
                        placeholder="Search for existing company..."
                      />
                      <div className="company-create-option">
                        <button
                          type="button"
                          className="btn-create-company-link"
                          onClick={() => setShowCreateCompany(!showCreateCompany)}
                        >
                          {showCreateCompany ? '−' : '+'} Create New Company
                        </button>
                        {showCreateCompany && (
                          <div className="form-group" style={{ marginTop: '0.5rem' }}>
                            <input
                              type="text"
                              placeholder="Enter company name"
                              value={newCompanyName}
                              onChange={(e) => setNewCompanyName(e.target.value)}
                              onFocus={() => setSelectedCompany(null)}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingClient ? 'Update' : 'Add'} Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company Form Modal */}
      {showCompanyForm && (
        <div className="modal-overlay" onClick={handleCloseCompanyForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCompany ? 'Edit Company' : 'Create New Company'}</h2>
              <button className="btn-close" onClick={handleCloseCompanyForm}>×</button>
            </div>
            <form onSubmit={handleCompanySubmit} className="client-form">
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    required
                    value={companyFormData.name}
                    onChange={(e) => setCompanyFormData({ ...companyFormData, name: e.target.value })}
                    placeholder="Enter company name..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Company Address</label>
                  <input
                    type="text"
                    value={companyFormData.address}
                    onChange={(e) => setCompanyFormData({ ...companyFormData, address: e.target.value })}
                    placeholder="Enter company address..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseCompanyForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingCompany ? 'Update' : 'Create'} Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
